import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'devextreme-react/button';
import { AXPCAssistant } from '../../components';
import { AXPCAssistantReferences } from '../../components';
import { AXPCChatHistory } from '../../components';
import { useAuth } from '../../contexts/auth';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { postAIChatData, searchAIKB, getAIChatHistory, getAIChatConversation } from '../../api/api';
import notify from 'devextreme/ui/notify';
import '../../components/axpc-assistant/AXPCAssistant.scss';
const parseErrorMessage = (errorStr) => {
  if (!errorStr) return "An unknown error occurred.";
  
  // Extract user-friendly error from Bedrock format: "...errors: path.to.field: Real Message"
  const errorsHeader = "The model returned the following errors: ";
  const headerIndex = errorStr.indexOf(errorsHeader);
  if (headerIndex !== -1) {
    const errorDetails = errorStr.substring(headerIndex + errorsHeader.length).trim();
    const colonIndex = errorDetails.indexOf(':');
    if (colonIndex !== -1) {
      return errorDetails.substring(colonIndex + 1).trim();
    }
    return errorDetails;
  }
  
  const generalPrefix = "operation: ";
  const generalIndex = errorStr.indexOf(generalPrefix);
  if (generalIndex !== -1) {
    return errorStr.substring(generalIndex + generalPrefix.length).trim();
  }
  
  return errorStr;
};

export default function AXPCAssistantPage() {
  const { user, getUser } = useAuth();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [allConversations, setAllConversations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);

  const assistant = useMemo(() => ({ id: 'assistant', name: 'AXPC Assistant' }), []);
  const currentUser = useMemo(() => ({ id: 'user', name: 'You' }), []);

  const initialMessages = useMemo(() => [
    {
      author: assistant,
      text: "**👋 Welcome to the AXPC Assistant**\nI can help you find, summarize, and draft content from our internal knowledge base — policy positions, regulatory filings, member data, and industry reports. I'll cite my sources where possible.\nNote: I may not always have the latest updates — please verify critical details against the original documents.\nChoose a prompt below or type your own question to get started.",
      timestamp: new Date()
    }
  ], [assistant]);

  useEffect(() => {
    if (user) {
      const groups = user.signInUserSession.idToken.payload['cognito:groups'];
      console.log(groups);
      if (groups && (groups.includes('admin') || groups.join().includes('aiuser') || groups.join().includes('kbmanager'))) {
        setIsAuthorized(true);
        getAIChatHistory(
          user,
          (data) => {
            setAllConversations(data.conversations);
            setSuggestions(data.suggestions || []);
            setMessages(initialMessages);
            setIsLoading(false);
          },
          (error) => {
            console.error('Error fetching chat history:', error);
            notify('Could not fetch chat history.', 'error', 5000);
            setIsLoading(false);
          }
        );
      } else {
        navigate('/home');
      }
    }
  }, [user, navigate]);

  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [references, setReferences] = useState([]);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());

  const newConversation = useCallback(async () => {
    setIsCreatingNewChat(true);
    // Wait for the previous conversation to be persisted
    await new Promise(resolve => setTimeout(resolve, 1000));

    getAIChatHistory(
      user,
      (data) => {
        setAllConversations(data.conversations);
        setSuggestions(data.suggestions || []);
        setSessionId(crypto.randomUUID());
        setMessages(initialMessages);
        setReferences([]);
        setConversationHistory([]);
        setIsCreatingNewChat(false);
      },
      (error) => {
        console.error('Error fetching chat history:', error);
        notify('Could not fetch chat history.', 'error', 5000);
        setIsCreatingNewChat(false);
      }
    );
  }, [initialMessages, user, setIsCreatingNewChat]);

  const onConversationSelected = useCallback((conversation) => {
    getAIChatConversation(
      user,
      conversation.sessionId,
      (history) => {
        const newMessages = [];
        const newConversationHistory = [];
        history.forEach((item, index) => {
          const author = item.role === 'user' ? currentUser : assistant;
          // Add a unique ID for historical messages
          newMessages.push({ id: `${conversation.sessionId}-${index}`, author, text: item.content, timestamp: new Date(item.timestamp) });
          newConversationHistory.push({ role: item.role, content: item.content });
        });

        setMessages(newMessages);
        setConversationHistory(newConversationHistory);
        setSessionId(conversation.sessionId);
        setReferences([]); // References are not stored in history, so clear them.
      },
      (error) => {
        console.error('Error fetching conversation:', error);
        notify('Could not fetch conversation history.', 'error', 5000);
      }
    );
  }, [user, assistant, currentUser]);

  const onMessageEntered = useCallback(async (e) => {
    const attachments = e.attachments || [];
    let textWithAttachments = e.message.text;
    if (attachments.length > 0) {
      const fileNames = attachments.map(f => f.name).join(', ');
      textWithAttachments = `[Attached file(s): ${fileNames}]\n\n${e.message.text || 'Analyze this document.'}`;
    }

    const newMessage = {
      author: currentUser,
      text: textWithAttachments,
      timestamp: new Date(),
    };

    setMessages(prevMessages => [...prevMessages, newMessage]);
    setTypingUsers([assistant]);

    if (user) {
      const assistantMessageId = Date.now();
      searchAIKB(
        user,
        { query: e.message.text || 'document', num_results: 10, history: conversationHistory },
        (refs) => {
          setReferences(refs);
          let assistantResponseText = '';
          postAIChatData(
            user,
            {
              prompt: e.message.text || 'Analyze this document.',
              session_id: sessionId,
              references: refs,
              history: conversationHistory,
              attachments: attachments
            },
            (chunk) => {
              if (chunk.error) {
                const friendlyError = parseErrorMessage(chunk.error);
                notify(friendlyError, 'error', 10000);
                assistantResponseText = `⚠️ **Error:** ${friendlyError}`;
                setMessages(prevMessages => {
                  const exists = prevMessages.some(msg => msg.id === assistantMessageId);
                  if (exists) {
                    return prevMessages.map(msg =>
                      msg.id === assistantMessageId ? { ...msg, text: assistantResponseText } : msg
                    );
                  } else {
                    return [...prevMessages, {
                      id: assistantMessageId,
                      author: assistant,
                      text: assistantResponseText,
                      timestamp: new Date(),
                    }];
                  }
                });
                return;
              }
              if (chunk.text) {
                const isFirstChunk = assistantResponseText === '';
                assistantResponseText += chunk.text;
                if (isFirstChunk) {
                  const assistantMessage = {
                    id: assistantMessageId,
                    author: assistant,
                    text: assistantResponseText,
                    timestamp: new Date(),
                  };
                  setMessages(prevMessages => [...prevMessages, assistantMessage]);
                } else {
                  setMessages(prevMessages => prevMessages.map(msg =>
                    msg.id === assistantMessageId ? { ...msg, text: assistantResponseText } : msg
                  ));
                }
              }
            },
            () => { // onDone
              setTypingUsers([]);
              setConversationHistory(h => [
                ...h,
                { role: 'user', content: textWithAttachments },
                { role: 'assistant', content: assistantResponseText },
              ].slice(-10));
            },
            (error) => { // onError
              notify("Error asking question.", 'error', 5000);
              setTypingUsers([]);
            }
          );
        },
        (error) => {
          notify("Error searching for references.", 'error', 5000);
          setTypingUsers([]);
          }
      );
    }
  }, [currentUser, assistant, sessionId, conversationHistory, getUser]);

  const onReferenceRemoved = useCallback(async (referenceToRemove) => {
    const updatedReferences = references.filter(ref => ref.uri !== referenceToRemove.uri);
    setReferences(updatedReferences);
    setTypingUsers([assistant]);

    if (user) {
      // Remove the last assistant message and user query from history
      const newConversationHistory = conversationHistory.slice(0, -2);
      const lastUserMessage = conversationHistory[conversationHistory.length - 2];

      if (!lastUserMessage) {
        // Not enough history to re-run the query, so just stop the typing indicator.
        setTypingUsers([]);
        return;
      }

      const assistantMessage = {
        id: Date.now(),
        author: assistant,
        text: '',
        timestamp: new Date(),
      };
      // Replace last assistant message with a new empty one
      setMessages(prevMessages => [...prevMessages.slice(0, -1), assistantMessage]);

      let assistantResponseText = '';
      postAIChatData(
        user,
        { prompt: lastUserMessage.content, session_id: sessionId, references: updatedReferences, history: newConversationHistory },
        (chunk) => {
          if (chunk.text) {
            assistantResponseText += chunk.text;
            setMessages(prevMessages => prevMessages.map(msg =>
              msg.id === assistantMessage.id ? { ...msg, text: assistantResponseText } : msg
            ));
          }
        },
        () => { // onDone
          setTypingUsers([]);
          setConversationHistory(h => [
            ...newConversationHistory,
            lastUserMessage,
            { role: 'assistant', content: assistantResponseText },
          ].slice(-10));
        },
        (error) => { // onError
          notify("Error asking question.", 'error', 5000);
          setTypingUsers([]);
        }
      );
    }
  }, [references, conversationHistory, assistant, sessionId, getUser]);

  const onConversationDeleted = useCallback((conversationId) => {
    // This is a placeholder for delete functionality.
    // You would typically make an API call to delete the conversation
    // and then update the `allConversations` state.
    notify(`Delete functionality for conversation ${conversationId} is not yet implemented.`, 'info', 3000);
  }, []);

  return (
    isAuthorized && <React.Fragment>
      <div className="">
        {isLoading ? (
          <div className="loading-container"><LoadIndicator height={40} width={40} /></div>
        ) : (
        <div className="row">
          <div className="col-md-8">
            <AXPCAssistant
              messages={messages}
              currentUser={currentUser}
              typingUsers={typingUsers}
              onMessageEntered={onMessageEntered}
              suggestions={suggestions}
            />
          </div>
          <div className="col-md-4">
            <div className="mb-3" style={{ height: '36px' }}>
              {isCreatingNewChat ? (
                <LoadIndicator height={36} width={36} />
              ) : (
                <Button
                  text="New Chat"
                  icon="fa fa-plus"
                  onClick={newConversation}
                />
              )}
            </div>
            <div className="axpc-assistant-container mb-3">
              <h4>Recent Conversations</h4>
              <AXPCChatHistory
                conversations={allConversations}
                onConversationSelected={onConversationSelected}
                onConversationDeleted={onConversationDeleted} />
            </div>
            
            <div className="axpc-assistant-container">
              <h4>References</h4>
              <AXPCAssistantReferences references={references} onReferenceRemoved={onReferenceRemoved} /> 
            </div>
            
          </div>
        </div>)}
      </div>
    </React.Fragment>
  );
}
